import {FormEvent, useEffect, useState} from "react";
import {Alert, Button, Card, Col, Form, Row} from "react-bootstrap";
import {Link, useNavigate, useParams, useSearchParams} from "react-router";
import {setVisitorAccess, SupportApi} from "../../api/SupportApi";
import {
    CustomerCloseReason,
    isOpenTicketState,
    SUPPORT_MESSAGE_MAX_LENGTH,
    SupportTicketState,
    SupportTicketStates,
    SupportTicketViewModel
} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {Field, FieldControl, FieldSelect} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDateTime} from "../../components/format";
import {announce} from "../../components/announce";
import {
    adminReasonsFor,
    AttachmentPicker,
    MessageThread,
    reasonLabel,
    StateHistory,
    SupportStateBadge,
    UploadProgress,
    useUploadProgress
} from "./SupportParts";
import {format, plural, press, rich, support} from "../../i18n";
import {PressRequestCard} from "./PressRequestCard";

const messages = support.ticket;

// One ticket: /support/:ticketId for its customer, /admin/support/:ticketId for administrators (adminView). The API
// answers 404 for anyone else's. Administrators can change its state and delete it; its customer can close it. A ticket
// opened signed out is its visitor's through the link the API emailed them (?access=), which this page sends with
// every call about the ticket.
export function SupportTicketPage({adminView}: { adminView?: boolean }) {
    const {ticketId = ''} = useParams();
    const [search] = useSearchParams();
    const access = adminView ? undefined : search.get('access') ?? undefined;
    const navigate = useNavigate();
    const {user, isAdmin, loading: authLoading} = useAuth();
    // Only while this page shows the ticket. (This effect runs before useAsync's, so before the first call.)
    useEffect(() => {
        setVisitorAccess(ticketId, access);
        return () => setVisitorAccess(ticketId, undefined);
    }, [ticketId, access]);
    const signedOutWithoutLink = !authLoading && !user && !access;
    const {data: ticket, error, loading, setData} = useAsync(
        () => signedOutWithoutLink ? Promise.resolve(undefined) : SupportApi.get(ticketId), [ticketId, access, signedOutWithoutLink]);
    const backTo = adminView ? '/admin/support' : '/support';

    // The account that opened it, or signed out, the visitor whose link opened it (the API showed it, so it's theirs).
    const isOwner = !!ticket && (user
        ? !!ticket.ownerUsername && user.username.toLowerCase() === ticket.ownerUsername.toLowerCase()
        : !!access && !ticket.ownerUsername);
    if (signedOutWithoutLink) {
        return (
            <>
                <PageHeader title={support.ticket.noSuchTicket}/>
                <Alert variant="secondary">{rich(messages.needsLink, { link: text => <Link to={`/login?returnTo=${encodeURIComponent(`/support/${ticketId}`)}`}>{text}</Link> })}</Alert>
            </>
        );
    }
    const open = !!ticket && isOpenTicketState(ticket.state);

    return (
        <>
            {(user || adminView) && <div className="mb-2"><Link to={backTo} className="small"><i className="bi bi-arrow-left me-1" aria-hidden="true"></i>{adminView ? messages.allTickets : messages.back}</Link></div>}
            <ErrorAlert error={error} messages={{ 404: access ? messages.linkExpired : messages.noSuchTicket }}/>
            {loading && !ticket && <LoadingSpinner/>}
            {ticket && (
                <>
                    <PageHeader title={ticket.subject}>
                        <SupportStateBadge state={ticket.state} className="fs-6"/>
                    </PageHeader>
                    <Row className="g-4">
                        <Col lg={8}>
                            <MessageThread ticketId={ticket.id} messages={ticket.messages} showAuthors={isAdmin} customerName={ticket.customerName}/>
                            {isAdmin || (isOwner && open) ? (
                                <ReplyForm ticket={ticket} onSent={setData}/>
                            ) : (
                                <Alert variant="secondary">
                                    {rich(messages.cantReply, { link: text => <Link to="/support/new">{text}</Link> },
                                        { state: support.statesInSentence[ticket.state] })}
                                </Alert>
                            )}
                        </Col>
                        <Col lg={4}>
                            <Card className="mb-4">
                                <Card.Body>
                                    <dl className="mb-0 support-details">
                                        <dt>{messages.details.product}</dt>
                                        <dd>
                                            {ticket.category === 'press' ? ticket.productName || press.contact.noProduct
                                                : ticket.licenseId ? ticket.productName || support.unknownProduct : support.generalTicket}
                                        </dd>
                                        {ticket.press && (
                                            <>
                                                <dt>{press.contact.publication}</dt>
                                                <dd>{ticket.press.publication}</dd>
                                            </>
                                        )}
                                        {isAdmin && (
                                            <>
                                                <dt>{messages.details.customer}</dt>
                                                <dd>
                                                    {ticket.ownerUsername || ticket.customerName}
                                                    {!ticket.ownerUsername && ticket.customerEmail && (
                                                        <div className="small text-body-secondary text-break">{format(messages.details.customerEmail, { email: ticket.customerEmail })}</div>
                                                    )}
                                                </dd>
                                                {ticket.licenseId && (
                                                    <>
                                                        <dt>{messages.details.license}</dt>
                                                        <dd><Link to={`/admin/licenses/${encodeURIComponent(ticket.licenseId)}`}>{messages.details.viewLicense}</Link></dd>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <dt>{messages.details.opened}</dt>
                                        <dd>{formatDateTime(ticket.createdOn)}</dd>
                                        <dt>{messages.details.lastActivity}</dt>
                                        <dd>{formatDateTime(ticket.lastActivityOn)}</dd>
                                    </dl>
                                </Card.Body>
                            </Card>
                            {isAdmin && ticket.press && <PressRequestCard ticket={ticket} onChanged={setData}/>}
                            {isAdmin && <StateForm ticket={ticket} onChanged={setData}/>}
                            {isOwner && open && <CloseTicket ticket={ticket} onClosed={setData}/>}
                            <Card className="mb-4">
                                <Card.Header as="h2" className="h6 mb-0">{support.history.title}</Card.Header>
                                <Card.Body><StateHistory history={ticket.stateHistory}/></Card.Body>
                            </Card>
                            {isAdmin && <DeleteTicket ticket={ticket} onDeleted={() => navigate(backTo)}/>}
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

function ReplyForm({ticket, onSent}: { ticket: SupportTicketViewModel, onSent(ticket: SupportTicketViewModel): void }) {
    const [body, setBody] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const upload = useUploadProgress();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!body.trim()) {
            return;
        }
        setBusy(true);
        setError(undefined);
        upload.start(files);
        try {
            onSent(await SupportApi.reply(ticket.id, body.trim(), files, upload.onProgress));
            setBody('');
            setFiles([]);
            setValidated(false);
            announce(messages.reply.sent);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            upload.done();
        }
    };

    return (
        <Card>
            <Card.Body>
                <Form noValidate onSubmit={submit}>
                    <ErrorAlert error={error} onClose={() => setError(undefined)} shownFields={['body']} messages={{
                        429: messages.reply.tooMany
                    }}/>
                    <Field className="mb-3" controlId="reply-body" label={messages.reply.label} error={(validated && !body.trim() && messages.reply.required) || fieldError(error, 'body')}>
                        <FieldControl as="textarea" rows={5} value={body} onChange={e => setBody(e.target.value)} maxLength={SUPPORT_MESSAGE_MAX_LENGTH} disabled={busy}/>
                    </Field>
                    <div className="mb-3"><AttachmentPicker files={files} onChange={setFiles} disabled={busy}/></div>
                    <SubmitButton busy={busy}>{messages.reply.submit}</SubmitButton>
                    <UploadProgress fraction={upload.fraction}/>
                </Form>
            </Card.Body>
        </Card>
    );
}

// Administrators: any state but Pending, with a reason for that state.
function StateForm({ticket, onChanged}: { ticket: SupportTicketViewModel, onChanged(ticket: SupportTicketViewModel): void }) {
    const targets = SupportTicketStates.filter(x => x !== 'pending' && x !== ticket.state);
    const [state, setState] = useState<SupportTicketState | ''>('');
    const [reason, setReason] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const reasons = adminReasonsFor(state, ticket.category);

    const pickState = (value: SupportTicketState | '') => {
        setState(value);
        setReason('');
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!state || !reason) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            onChanged(await SupportApi.changeState(ticket.id, state, reason));
            announce(format(messages.changeState.changed, { state: support.states[state] }));
            setState('');
            setReason('');
            setValidated(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mb-4">
            <Card.Header as="h2" className="h6 mb-0">{messages.changeState.title}</Card.Header>
            <Card.Body>
                <Form noValidate onSubmit={submit}>
                    <ErrorAlert error={error} onClose={() => setError(undefined)}/>
                    <Field className="mb-3" controlId="ticket-state" label={messages.changeState.newStatus} error={validated && !state && messages.changeState.statusRequired}>
                        <FieldSelect value={state} onChange={e => pickState(e.target.value as SupportTicketState | '')} disabled={busy}>
                            <option value="">{messages.changeState.chooseStatus}</option>
                            {targets.map(x => <option key={x} value={x}>{support.states[x]}</option>)}
                        </FieldSelect>
                    </Field>
                    <Field className="mb-3" controlId="ticket-reason" label={messages.changeState.reason} error={validated && !!state && !reason && messages.changeState.reasonRequired}
                           help={!state ? messages.changeState.statusFirst : undefined}>
                        <FieldSelect value={reason} onChange={e => setReason(e.target.value)} disabled={busy || !state}>
                            <option value="">{messages.changeState.chooseReason}</option>
                            {reasons.map(x => <option key={x.id} value={x.id}>{reasonLabel(x.id)}</option>)}
                        </FieldSelect>
                    </Field>
                    <SubmitButton busy={busy}>{messages.changeState.submit}</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}

function CloseTicket({ticket, onClosed}: { ticket: SupportTicketViewModel, onClosed(ticket: SupportTicketViewModel): void }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const close = async () => {
        setBusy(true);
        setError(undefined);
        try {
            onClosed(await SupportApi.changeState(ticket.id, 'closed', CustomerCloseReason));
            announce(messages.close.closed);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    return (
        <div className="mb-4">
            <ErrorAlert error={error} onClose={() => setError(undefined)}/>
            <Button variant="outline-secondary" className="w-100" onClick={() => setConfirming(true)}>
                <i className="bi bi-check2-circle me-1" aria-hidden="true"></i>{messages.close.button}
            </Button>
            <ConfirmModal show={confirming} title={messages.close.title} confirmLabel={messages.close.button} variant="primary" busy={busy} onConfirm={close}
                          onCancel={() => setConfirming(false)}>
                <p className="mb-0">{messages.close.text}</p>
            </ConfirmModal>
        </div>
    );
}

function DeleteTicket({ticket, onDeleted}: { ticket: SupportTicketViewModel, onDeleted(): void }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const remove = async () => {
        setBusy(true);
        setError(undefined);
        try {
            await SupportApi.delete(ticket.id);
            announce(messages.delete.deleted);
            onDeleted();
        } catch (err) {
            setError(err);
            setBusy(false);
            setConfirming(false);
        }
    };

    const attachmentCount = ticket.messages.reduce((total, x) => total + x.attachments.length, 0);
    return (
        <div>
            <ErrorAlert error={error} onClose={() => setError(undefined)} messages={{ 404: messages.delete.alreadyDeleted }}/>
            <Button variant="outline-danger" className="w-100" onClick={() => setConfirming(true)}>
                <i className="bi bi-trash me-1" aria-hidden="true"></i>{messages.delete.button}
            </Button>
            <ConfirmModal show={confirming} title={messages.delete.title} confirmLabel={messages.delete.button} busy={busy} onConfirm={remove} onCancel={() => setConfirming(false)}>
                <p className="mb-0">
                    {format(messages.delete.text, {
                        messages: plural(ticket.messages.length, messages.delete.messages),
                        attachments: attachmentCount > 0 ? plural(attachmentCount, messages.delete.attachments) : ''
                    })}
                </p>
            </ConfirmModal>
        </div>
    );
}
